import { getEnvVars } from '@order/utils';

import { ONE_TRUST_CONSENT_CHANGED_EVENT } from './consent.constants';
import { type OneTrustCategory } from './consent.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Runs the callback once the visitor allows the OneTrust category.
 *
 * Opt-out banners activate every category as soon as OneTrust loads, so there
 * the callback only waits for the SDK. Opt-in banners keep it pending until the
 * visitor accepts the category. Without OneTrust (no domain key or the
 * `HideCookiesBanner` cookie) the callback runs right away.
 */
export function runWhenOneTrustCategoryActive(params: Params) {
  const { category, callback } = params;

  if (checkIfOneTrustCategoryActive(category)) {
    callback();

    return;
  }

  function handleConsentChange() {
    if (!checkIfOneTrustCategoryActive(category)) {
      return;
    }

    window.removeEventListener(ONE_TRUST_CONSENT_CHANGED_EVENT, handleConsentChange);
    callback();
  }

  window.addEventListener(ONE_TRUST_CONSENT_CHANGED_EVENT, handleConsentChange);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfOneTrustCategoryActive(category: OneTrustCategory) {
  if (!checkIfOneTrustEnabled()) {
    return true;
  }

  const activeGroups = (window as OneTrustWindow).OnetrustActiveGroups ?? '';

  return activeGroups.split(',').includes(category);
}

function checkIfOneTrustEnabled() {
  const hasDomainKey = Boolean(ONE_TRUST_SCRIPT_DOMAIN_KEY);
  const isOneTrustSkipped = document.cookie.includes('HideCookiesBanner');

  return hasDomainKey && !isOneTrustSkipped;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const { ONE_TRUST_SCRIPT_DOMAIN_KEY } = getEnvVars();

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = { category: OneTrustCategory; callback: () => void };

type OneTrustWindow = Window & { OnetrustActiveGroups?: string };
